<template>
  <div>
    <div class="page-head">
      <div>
        <h2>酒店管理</h2>
        <p>维护酒店推荐列表中的酒店名称、等级、区域和介绍。</p>
      </div>
      <el-button type="primary" @click="openCreate">新增酒店</el-button>
    </div>

    <el-card shadow="never">
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item label="关键词"><el-input v-model="filters.keyword" placeholder="酒店名称/等级/区域" clearable /></el-form-item>
        <el-form-item label="展示状态">
          <el-select v-model="filters.status" placeholder="全部" clearable>
            <el-option label="展示中" value="active" />
            <el-option label="已隐藏" value="inactive" />
          </el-select>
        </el-form-item>
        <el-form-item><el-button type="primary" @click="fetchList">查询</el-button><el-button @click="reset">重置</el-button></el-form-item>
      </el-form>

      <paged-table :rows="rows" :loading="loading" :page="page" :page-size="pageSize" :total="total" @page-change="page = $event; fetchList()" @size-change="pageSize = $event; page = 1; fetchList()">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column label="预览" width="110">
          <template slot-scope="{ row }">
            <el-image v-if="row.cover_url" class="hotel-thumb" :src="row.cover_url" fit="cover" />
            <span v-else class="muted">未上传</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="酒店名称" min-width="180" />
        <el-table-column prop="level" label="等级" width="110" />
        <el-table-column prop="area" label="区域" width="120" />
        <el-table-column label="标签" min-width="180">
          <template slot-scope="{ row }">
            <el-tag v-for="tag in normalizeTags(row.tags_json)" :key="tag" size="mini" class="table-tag">{{ tag }}</el-tag>
            <span v-if="!normalizeTags(row.tags_json).length" class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="小程序展示" width="160">
          <template slot-scope="{ row }"><publish-switch :value="row.status" @change="changeStatus(row, $event)" /></template>
        </el-table-column>
        <el-table-column prop="sort_order" label="排序" width="130">
          <template slot-scope="{ row }"><sort-input :value="Number(row.sort_order || 0)" @change="changeSort(row, $event)" /></template>
        </el-table-column>
        <el-table-column label="操作" width="150">
          <template slot-scope="{ row }">
            <el-button type="text" @click="openEdit(row)">编辑</el-button>
            <el-button type="text" class="danger-text" @click="remove(row)">删除</el-button>
          </template>
        </el-table-column>
      </paged-table>
    </el-card>

    <el-dialog :title="drawerMode === 'create' ? '新增酒店' : '编辑酒店'" :visible.sync="drawerVisible" width="600px" destroy-on-close>
      <el-form ref="form" class="drawer-form" :model="form" :rules="rules" label-width="120px">
        <el-form-item label="酒店名称" prop="name"><el-input v-model="form.name" /></el-form-item>
        <el-form-item label="等级">
          <el-select v-model="form.level" clearable>
            <el-option label="经济" value="经济" />
            <el-option label="舒适" value="舒适" />
            <el-option label="高端" value="高端" />
            <el-option label="豪华" value="豪华" />
          </el-select>
        </el-form-item>
        <el-form-item label="区域"><el-input v-model="form.area" /></el-form-item>
        <el-form-item label="封面图" prop="cover_url"><asset-upload v-model="form.cover_url" type="image" category="hotels" /></el-form-item>
        <el-form-item label="详情图"><asset-upload v-model="form.detail_image_url" type="image" category="hotels" /></el-form-item>
        <el-form-item label="详细地址"><el-input v-model="form.address" /></el-form-item>
        <el-form-item label="服务设施"><el-input v-model="servicesText" type="textarea" :rows="3" placeholder="一行一个，例如：含早餐" /></el-form-item>
        <el-form-item label="适合人群"><el-input v-model="form.suitable_people" /></el-form-item>
        <el-form-item label="旅行类型"><el-input v-model="form.trip_type" /></el-form-item>
        <el-form-item label="酒店标签">
          <div class="tag-editor">
            <el-tag v-for="tag in form.tags_json" :key="tag" closable @close="removeTag(tag)">{{ tag }}</el-tag>
            <el-input
              v-if="tagInputVisible"
              ref="tagInput"
              v-model="tagInput"
              class="tag-input"
              size="small"
              @keyup.enter.native="confirmTag"
              @blur="confirmTag"
            />
            <el-button v-else size="small" @click="showTagInput">添加标签</el-button>
          </div>
        </el-form-item>
        <el-form-item label="酒店简介"><el-input v-model="form.description" type="textarea" :rows="5" /></el-form-item>
      </el-form>
      <div class="drawer-footer">
        <el-button @click="drawerVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import AssetUpload from "../components/AssetUpload.vue";
import PagedTable from "../components/PagedTable.vue";
import PublishSwitch from "../components/PublishSwitch.vue";
import SortInput from "../components/SortInput.vue";
import { changeResourceStatus, createResource, deleteResource, listResource, updateResource } from "../api/resources";

export default {
  name: "HotelsManage",
  components: { AssetUpload, PagedTable, PublishSwitch, SortInput },
  data() {
    return {
      loading: false,
      saving: false,
      drawerVisible: false,
      drawerMode: "create",
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: { keyword: "", status: "" },
      servicesText: "",
      tagInput: "",
      tagInputVisible: false,
      form: this.emptyForm(),
      rules: {
        name: [{ required: true, message: "请填写酒店名称", trigger: "blur" }],
        cover_url: [{ required: true, message: "请上传封面图", trigger: "change" }]
      }
    };
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    emptyForm() {
      return {
        name: "",
        level: "",
        area: "",
        cover_url: "",
        detail_image_url: "",
        address: "",
        services_json: [],
        suitable_people: "",
        trip_type: "",
        tags_json: [],
        description: ""
      };
    },
    normalizeTags(value) {
      if (Array.isArray(value)) return value.filter(Boolean);
      if (!value) return [];
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch (error) {
        return String(value)
          .split(/[，,]/)
          .map((item) => item.trim())
          .filter(Boolean);
      }
    },
    async fetchList() {
      this.loading = true;
      try {
        const res = await listResource("hotels", { ...this.filters, page: this.page, pageSize: this.pageSize });
        this.rows = res.data && res.data.items ? res.data.items : [];
        this.total = res.data && res.data.total ? res.data.total : 0;
      } finally {
        this.loading = false;
      }
    },
    reset() {
      this.filters = { keyword: "", status: "" };
      this.page = 1;
      this.fetchList();
    },
    openCreate() {
      this.drawerMode = "create";
      this.form = this.emptyForm();
      this.servicesText = "";
      this.tagInput = "";
      this.tagInputVisible = false;
      this.drawerVisible = true;
    },
    openEdit(row) {
      this.drawerMode = "edit";
      this.form = { ...this.emptyForm(), ...row };
      const services = Array.isArray(row.services_json)
        ? row.services_json
        : typeof row.services_json === "string"
          ? this.parseJson(row.services_json)
          : [];
      this.servicesText = services.map((item) => item.title || item.name || item).join("\n");
      this.form.tags_json = this.normalizeTags(row.tags_json);
      this.tagInput = "";
      this.tagInputVisible = false;
      this.drawerVisible = true;
    },
    showTagInput() {
      this.tagInputVisible = true;
      this.$nextTick(() => {
        if (this.$refs.tagInput) this.$refs.tagInput.focus();
      });
    },
    confirmTag() {
      const value = this.tagInput.trim();
      if (value && !this.form.tags_json.includes(value)) {
        this.form.tags_json.push(value);
      }
      this.tagInput = "";
      this.tagInputVisible = false;
    },
    removeTag(tag) {
      this.form.tags_json = this.form.tags_json.filter((item) => item !== tag);
    },
    save() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return;
        this.saving = true;
        try {
          const payload = { ...this.form, services_json: this.servicesText.split("\n").map((item) => item.trim()).filter(Boolean) };
          if (this.drawerMode === "create") await createResource("hotels", payload);
          else await updateResource("hotels", this.form.id, payload);
          this.$message.success("酒店已保存");
          this.drawerVisible = false;
          this.fetchList();
        } finally {
          this.saving = false;
        }
      });
    },
    async changeStatus(row, status) {
      await changeResourceStatus("hotels", row.id, status);
      this.$message.success(status === "active" ? "酒店已展示" : "酒店已隐藏");
      this.fetchList();
    },
    async changeSort(row, sortOrder) {
      await updateResource("hotels", row.id, { ...row, sort_order: sortOrder });
      this.$message.success("排序已更新");
      this.fetchList();
    },
    remove(row) {
      this.$confirm("确定删除该酒店吗？", "删除酒店", { type: "warning" })
        .then(async () => {
          await deleteResource("hotels", row.id);
          this.$message.success("酒店已删除");
          this.fetchList();
        })
        .catch(() => {});
    },
    parseJson(value) {
      try {
        return JSON.parse(value || "[]");
      } catch (error) {
        return [];
      }
    }
  }
};
</script>

<style scoped>
.hotel-thumb {
  width: 72px;
  height: 48px;
  border-radius: 6px;
  background: #f2f3f5;
}

.table-tag {
  margin: 0 6px 6px 0;
}

.tag-editor {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.tag-input {
  width: 110px;
}

.muted {
  color: #909399;
}
</style>
